import { useCallback } from "react";
import { INGActionEditorProps, INGActionEditorRendererProps } from "../../library/NGFieldExtensions";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import type { Edge } from "@xyflow/react";
import { XYFlowContext } from "./xyflow-context";
import { Autocomplete, IconButton, Stack, TextField, Typography, Checkbox } from "@mui/material";
import Icon from "../NGMaterialIcon/NGMaterialIcon";
import ActionNode from "./NGActionNode";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import {
  defaultTriggers,
  listTriggers,
  propagateTriggers,
  defaultXPosition,
  Handles,
  NextActionType,
} from "../../library/actionEditor";
import { generateGuid } from "../../library/utils";
import React, { useEffect } from "react";
import ActionDialog from "./ActionDialog";
import NGCodeEditor from "../NGCodeEditor/NGCodeEditor";
import CustomSwitch from "../NGCustomSwitch/NGCustomSwitch";
import CustomEdge from "./NGActionEdge";
import NGUndoRedoToolbar from "../NGUndoRedoToolbar/NGUndoRedoToolbar";
import debounce from "lodash-es/debounce";

const nodeTypes = {
  default: ActionNode,
};

const edgeTypes = {
  default: CustomEdge,
  buttonedge: CustomEdge,
};

let currentPosition: object | null = null;

const defaultNodeStyle = { width: "300px" };

const NGActionEditor = ({ config, context }: INGActionEditorProps) => {
  const xyflow: any = useSignal(null);
  useEffect(() => {
    (async () => {
      await import("@/lib/xyflow/style.css");
      const mod = await import("@/lib/xyflow/xyflow-bundle.es.js");
      xyflow.value = mod.ReactFlow;
    })();
  }, []);

  if (!xyflow.value) return <div>Loading flow…</div>;

  return <NGActionEditorRender config={config} context={context} xyflow={xyflow.value} />;
};

const NGActionEditorRender = ({ config, context, xyflow }: INGActionEditorRendererProps) => {
  const local = setupLocalState(config, { Value: useSignal(config.Value ?? { Actions: [] }) }, context);
  const handlers = setupHandlers(config, context);
  const deleteActionsDialogOpen = useSignal(false);
  const viewCode = useSignal(false);

  let triggers = local.Value.value.Actions;

  const getInitialTrigger = () => {
    let initialTrigger = triggers?.[0];

    if (initialTrigger == null) {
      let initialTriggerName = defaultTriggers[0];
      switch (local.Value.value?.ElementType) {
        case "Component":
        case "Page":
          initialTriggerName = "onLoad";
          break;
        case "InputField":
        case "MultiSelect":
        case "Checkbox":
        case "Radio":
        case "Switch":
        case "Slider":
          initialTriggerName = "onChange";
          break;
        case "List":
          initialTriggerName = "onRowClick";
          break;
      }

      initialTrigger = { Trigger: initialTriggerName, CommandSet: { Commands: [] } };
    }

    if (local.Value.value.Trigger) {
      return triggers.find((t) => t.Trigger === local.Value.value.Trigger) ?? initialTrigger;
    }

    return initialTrigger;
  };

  if (local.Value.value.ElementType === "List") {
    if (!defaultTriggers.includes("onSingleRowUpdate")) {
      defaultTriggers.push(...listTriggers);
    }
  }

  const selectedTrigger = useSignal(getInitialTrigger());
  const getCurrentTriggerIndex = () => triggers?.findIndex((t) => t.Trigger === selectedTrigger.value.Trigger);
  const actionNodes = useComputed(
    () =>
      local.Value.value?.Actions?.find((x) => x.Trigger === selectedTrigger.value.Trigger)?.CommandSet?.Commands ?? []
  );

  if (!selectedTrigger.value.CommandSet?.Commands) {
    selectedTrigger.value = { ...selectedTrigger.value, CommandSet: { Commands: [] } };
  }

  const onDeleteActionNode = (id) => {
    const updatedNodes = actionNodes.value.filter((an) => an.Id !== id);

    updatedNodes.forEach((node) => {
      if (node.NextCommandIdOnFailure === id) delete node.NextCommandIdOnFailure;
      if (node.NextCommandIdOnSuccess === id) delete node.NextCommandIdOnSuccess;
    });

    const deleteTrigger = updatedNodes.length === 0;
    saveCurrentTrigger(updatedNodes, deleteTrigger);
  };

  const handleSave = debounce(
    (_handlerName?, _action?, _event?, _data?, _context?) => {
      (handlers as any).onSave({}, { Actions: triggers });
    },
    1000,
    { leading: true }
  );

  const handleOnConnectNodes = (e, commandType, swapNextCommand?) => {
    const updatedNodes = [...actionNodes.value];
    const sourceNode = updatedNodes.find((an) => an.Id === e.source);

    if (e.sourceHandle === Handles.START) {
      const startEdgeIndex = edges.findIndex((edge) => edge.sourceHandle === "startHandle");
      if (startEdgeIndex >= 0) {
        edges.splice(startEdgeIndex, 1);
        setEdges(edges);
      }
      selectedTrigger.value = {
        ...selectedTrigger.value,
        CommandSet: { ...selectedTrigger.value.CommandSet, FirstCommandId: e.target },
      };
    } else if (commandType === NextActionType.OK && e.target) {
      sourceNode.NextCommandIdOnSuccess = e.target;
    } else if (commandType === NextActionType.FAIL && e.target) {
      sourceNode.NextCommandIdOnFailure = e.target;
    }

    if (e.sourceHandle === Handles.RIGHT) {
      sourceNode.Editor = {
        ...sourceNode.Editor,
        Handles: { ...(sourceNode.Editor?.Handles ?? {}), [Handles.RIGHT]: commandType },
      };
    } else if (e.sourceHandle === Handles.BOTTOM) {
      sourceNode.Editor = {
        ...sourceNode.Editor,
        Handles: { ...(sourceNode.Editor?.Handles ?? {}), [Handles.BOTTOM]: commandType },
      };
    }

    if (swapNextCommand) {
      const nextCommandIdOnSuccess = sourceNode.NextCommandIdOnSuccess;
      sourceNode.NextCommandIdOnSuccess = sourceNode.NextCommandIdOnFailure;
      sourceNode.NextCommandIdOnFailure = nextCommandIdOnSuccess;
    }

    onConnect(e);
    saveCurrentTrigger(updatedNodes);
  };

  const startNode = {
    id: "startNode",
    position: { x: defaultXPosition - 200, y: 0 },
    data: { onConnect: handleOnConnectNodes, actions: local.Value.value.Actions },
    style: { width: 60, height: 40, border: "1px solid #6495ed" },
  };

  const getNodesAndEdges = useComputed(() => {
    let xPosition = defaultXPosition;

    const newNodes: any[] = [];
    const newEdges: Edge<any>[] = [];

    actionNodes.value.forEach(
      (
        { Id, Instruction, Editor, NextCommandIdOnSuccess, NextCommandIdOnFailure, SkipCommand, Parameters },
        index,
        arr
      ) => {
        newNodes.push({
          id: Id,
          data: {
            action: { ...Instruction, Handles: Editor?.Handles, SkipCommand, Parameters },
            index,
            context,
            onDeleteActionNode,
            onChangeSkipCommand,
            currentTriggerIndex: getCurrentTriggerIndex(),
            onSave: handleSave,
            onConnect: handleOnConnectNodes,
            actions: local.Value.value.Actions,
          },
          position: { x: Editor?.Position?.x ?? xPosition, y: Editor?.Position?.y ?? 20 },
          style: defaultNodeStyle,
        });

        if (!Editor?.Position?.X) xPosition += 320;

        if (NextCommandIdOnSuccess) {
          const nextCommand = arr.find(({ Id }) => Id === NextCommandIdOnSuccess);
          if (nextCommand)
            newEdges.push({
              id: `${Id}-${nextCommand.Id}`,
              source: Id,
              target: nextCommand.Id,
              sourceHandle: Editor?.Handles?.[Handles.RIGHT] === NextActionType.OK ? Handles.RIGHT : Handles.BOTTOM,
            });
        }

        if (NextCommandIdOnFailure) {
          const nextCommand = arr.find(({ Id }) => Id === NextCommandIdOnFailure);
          if (nextCommand)
            newEdges.push({
              id: `${Id}-${nextCommand.Id}`,
              source: Id,
              target: nextCommand.Id,
              sourceHandle: Editor?.Handles?.[Handles.RIGHT] === NextActionType.FAIL ? Handles.RIGHT : Handles.BOTTOM,
            });
        }
      }
    );

    const startAction = actionNodes.value.find((node) => node.Id === selectedTrigger.value.CommandSet?.FirstCommandId);

    if (startAction) {
      newEdges.push({
        id: generateGuid(),
        source: startNode.id,
        target: startAction.Id,
        sourceHandle: "startHandle",
      });
    }

    return { newNodes, newEdges };
  });

  const { ReactFlow, Background, BackgroundVariant, Controls, addEdge, useEdgesState, useNodesState } = xyflow;

  const [nodes, setNodes, onNodesChange] = useNodesState([startNode] as any[]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge<any>[]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  useSignalEffect(() => {
    const { newNodes, newEdges } = getNodesAndEdges.value;

    setNodes([startNode, ...newNodes]);
    setEdges(newEdges);
  });

  const saveCurrentTrigger = (updatedNodes = actionNodes.value, deleteCurrentTrigger = false) => {
    const triggersToUpdate = triggers ?? [];
    const triggerIndexToUpdate = triggersToUpdate.findIndex((t) => t.Trigger === selectedTrigger.value.Trigger);

    if (triggerIndexToUpdate < 0) {
      selectedTrigger.value.CommandSet = {
        Commands: updatedNodes,
        Id: generateGuid(),
        FirstCommandId: updatedNodes[0]?.Id,
      };
      triggersToUpdate.push({ ...selectedTrigger.value });
    } else if (deleteCurrentTrigger) {
      triggersToUpdate.splice(triggerIndexToUpdate, 1);
    } else {
      triggersToUpdate[triggerIndexToUpdate] = {
        ...selectedTrigger.value,
        CommandSet: { ...selectedTrigger.value.CommandSet, Commands: [...updatedNodes] },
      };
    }
    local.Value.value = { ...local.Value.value, Actions: [...triggersToUpdate] };
    handleSave();
  };

  const handleAddNewNode = () => {
    const newNodeId = generateGuid();
    const defaultInitAction = "SetState";
    const triggerIndex = getCurrentTriggerIndex();

    const startPosition = { x: 0, y: 0 };

    setNodes([
      ...nodes,
      {
        id: newNodeId,
        position: { ...startPosition },
        data: {
          action: { Name: defaultInitAction },
          index: actionNodes.value.length,
          context,
          onDeleteActionNode,
          onChangeSkipCommand,
          onConnect: handleOnConnectNodes,
          currentTriggerIndex: triggerIndex >= 0 ? triggerIndex : 0,
          onSave: handleSave,
        },
        style: defaultNodeStyle,
      },
    ]);
    const updatedNodes = [
      ...actionNodes.value,
      {
        Id: newNodeId,
        Instruction: { Name: defaultInitAction },
        Parameters: [],
        Editor: { Position: { ...startPosition } },
      },
    ];

    saveCurrentTrigger(updatedNodes);
  };

  const handleChangeTrigger = (e: React.SyntheticEvent<Element, Event>, value) => {
    const newValue = value?.label ?? value ?? "";
    const triggerIndex = triggers.findIndex((trigger) => trigger.Trigger === newValue);
    selectedTrigger.value = triggers[triggerIndex] ?? {
      Trigger: newValue,
      CommandSet: { Commands: [] },
    };

    if (triggerIndex === -1) {
      triggers = [...triggers, selectedTrigger.value];
    }

    saveCurrentTrigger();
  };

  const handleClearAllNodes = () => {
    deleteActionsDialogOpen.value = true;
  };

  const onConfirmClearAllNodes = () => {
    setNodes([startNode]);
    setEdges([]);

    saveCurrentTrigger([], true);

    deleteActionsDialogOpen.value = false;
  };

  const handleChangePropagateToParent = (e, value) => {
    if (value) {
      selectedTrigger.value = {
        ...selectedTrigger.value,
        PropagateToParent: { Trigger: "onViewComplete", PropagateOnFailure: false },
      };
    } else {
      delete selectedTrigger.value.PropagateToParent;
    }

    saveCurrentTrigger();
  };

  const handleChangePropagateOnFailure = (e, value) => {
    selectedTrigger.value = {
      ...selectedTrigger.value,
      PropagateToParent: { ...selectedTrigger.value.PropagateToParent, PropagateOnFailure: value },
    };

    saveCurrentTrigger();
  };

  const handleChangePropagateToParentTrigger = (e, value) => {
    selectedTrigger.value = {
      ...selectedTrigger.value,
      PropagateToParent: { ...selectedTrigger.value.PropagateToParent, Trigger: value.label },
    };

    saveCurrentTrigger();
  };

  const handleCodeEditorChange = (e, newValue) => {
    local.Value.value = newValue;
    handleSave();
  };

  const handleOnNodesChange = (e) => {
    if (e[0]?.dragging) {
      currentPosition = e[0].position;
    } else if (e[0]?.type === "position" && currentPosition) {
      const updatedNodes = [...actionNodes.value];
      const actionNode = updatedNodes.find((node) => node.Id === e[0].id);
      actionNode.Editor = { ...actionNode.Editor, Position: { ...currentPosition } };
      currentPosition = null;
      saveCurrentTrigger(updatedNodes);
    }

    onNodesChange(e);
  };

  const deleteEdge = (sourceAction, target, handle) => {
    if (sourceAction.Editor?.Handles?.[handle] === NextActionType.OK && target === sourceAction?.[NextActionType.OK]) {
      delete sourceAction[NextActionType.OK];
      delete sourceAction.Editor?.Handles?.[handle];
    } else if (
      sourceAction.Editor?.Handles?.[handle] === NextActionType.FAIL &&
      target === sourceAction?.[NextActionType.FAIL]
    ) {
      delete sourceAction[NextActionType.FAIL];
      delete sourceAction.Editor?.Handles?.[handle];
    }
  };

  const handleOnEdgesChange = (e) => {
    const updatedNodes = actionNodes.value;
    const edgeModified = e[0];
    const edge = edges.find((e) => e.id === edgeModified.id);
    if (edgeModified.type === "remove" && edge) {
      const sourceAction = updatedNodes.find((an) => an.Id === edge.source);
      if (edge.sourceHandle === Handles.BOTTOM) deleteEdge(sourceAction, edge.target, Handles.BOTTOM);
      if (edge.sourceHandle === Handles.RIGHT) deleteEdge(sourceAction, edge.target, Handles.RIGHT);

      saveCurrentTrigger(updatedNodes);
    }

    onEdgesChange(e);
  };

  const onChangeSkipCommand = (checked, id, inputValue) => {
    const updatedNodes = [...actionNodes.value];
    const actionNode = updatedNodes.find((node) => node.Id === id);

    if (checked) actionNode.SkipCommand = inputValue;
    else delete actionNode.SkipCommand;

    saveCurrentTrigger(updatedNodes);
  };

  return (
    <Stack direction="column" display="flex" width="100%" height="100%" gap="1rem" id="actionEditorContainer">
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap="2rem">
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <Typography>Trigger:</Typography>
            <Autocomplete
              id="action-type-selector"
              data-testid="action-type-selector"
              size="small"
              disableClearable
              freeSolo
              options={defaultTriggers.map((trigger) => ({ label: trigger }))}
              sx={{ width: 300, ".MuiFormControl-root": { marginTop: 0 } }}
              renderInput={(params) => <TextField {...params} />}
              value={{ label: selectedTrigger.value.Trigger ?? "" }}
              onChange={handleChangeTrigger}
            />
            <IconButton color="inherit" size="small" onClick={handleAddNewNode}>
              <Icon name="add" />
            </IconButton>
            <IconButton disabled={!nodes.length} color="inherit" size="small" onClick={handleClearAllNodes}>
              <Icon name="delete" />
            </IconButton>
          </Stack>
          <NGUndoRedoToolbar
            config={{
              Id: "actions-editor-undo-redo-toolbar",
              Bindings: { TrackedObject: "{ ActionEditor: State.ActionEditor }" },
              Actions: [
                {
                  Trigger: "onChange",
                  postHandler: handleSave,
                  CommandSet: {
                    FirstCommandId: "1",
                    Commands: [
                      {
                        Id: "1",
                        Instruction: {
                          Name: "SetState",
                        },
                        Parameters: [
                          {
                            Name: "Bindings",
                            Value: {
                              "Global.NGForm[State.ActionEditor.Form].Actions": "State.ActionEditor.Actions",
                            },
                          },
                        ],
                      },
                    ],
                  },
                },
              ],
            }}
            context={context}
          />
          <Stack direction="row" gap="0.5rem" alignItems="center">
            <Typography>Propagate to Parent</Typography>
            <Checkbox
              onChange={handleChangePropagateToParent}
              data-testid="propagateToParentCheckbox"
              checked={!!selectedTrigger.value.PropagateToParent}
              disabled={!selectedTrigger.value.Trigger}
            />
            <Typography>Trigger</Typography>
            <Autocomplete
              id="action-type-selector"
              data-testid="action-type-selector"
              size="small"
              disableClearable
              freeSolo
              disabled={!selectedTrigger.value.PropagateToParent}
              options={propagateTriggers.map((trigger) => ({ label: trigger }))}
              sx={{ width: 300, ".MuiFormControl-root": { marginTop: 0 } }}
              renderInput={(params) => <TextField {...params} />}
              value={{ label: selectedTrigger.value.PropagateToParent?.Trigger ?? "" }}
              onChange={handleChangePropagateToParentTrigger}
            />
          </Stack>
          <Typography>Propagate on Failure</Typography>
          <Checkbox
            onChange={handleChangePropagateOnFailure}
            data-testid="propagateToOnFailureCheckbox"
            checked={!!selectedTrigger.value.PropagateToParent?.PropagateOnFailure}
            disabled={!selectedTrigger.value.PropagateToParent}
          />
          <CustomSwitch
            checked={viewCode.value}
            onChange={(e, value) => {
              viewCode.value = value;
            }}
            icon={<Icon name="code" fontSize="small" />}
            checkedIcon={<Icon name="code" fontSize="small" />}
            sx={{
              marginBottom: "3px",
            }}
          />
        </Stack>
      </Stack>
      {viewCode.value ? (
        <NGCodeEditor
          config={{
            ...config,
            Height: "100%",
            Width: "100%",
            OnChange: handleCodeEditorChange,
          }}
          context={context}
        />
      ) : (
        <XYFlowContext.Provider value={xyflow}>
          <ReactFlow
            nodes={nodes}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            edges={edges}
            onNodesChange={handleOnNodesChange}
            onEdgesChange={handleOnEdgesChange}
            proOptions={{ hideAttribution: true }}
            deleteKeyCode={null}
          >
            <Controls />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </XYFlowContext.Provider>
      )}

      <ActionDialog
        open={deleteActionsDialogOpen}
        onCancel={() => {
          deleteActionsDialogOpen.value = false;
        }}
        onConfirm={onConfirmClearAllNodes}
        title="Confirm delete all actions"
      />
    </Stack>
  );
};

export default NGActionEditor;
